import type { ReliabilityProofPackPreviewResponse, ReportVisual } from '../services/reportsApi';
import { REPORT_KIND_LABELS } from './reportFlow';

const ACCEPTANCE_PROOF_CACHE_PREFIX = 'acceptance.proofPreview.v1';

export function getAcceptanceProofCacheKey(orgId: string, siteId: string, deviceId?: string, scopeKey?: string): string {
  const normalizedScopeKey = String(scopeKey || '').trim();
  const normalizedDeviceId = String(deviceId || '').trim() || 'site';
  return normalizedScopeKey
    ? `${ACCEPTANCE_PROOF_CACHE_PREFIX}:${orgId}:${siteId}:${normalizedDeviceId}:${normalizedScopeKey}`
    : `${ACCEPTANCE_PROOF_CACHE_PREFIX}:${orgId}:${siteId}:${normalizedDeviceId}`;
}

export function readAcceptanceProofCache(
  orgId: string,
  siteId: string,
  deviceId?: string,
  scopeKey?: string
): ReliabilityProofPackPreviewResponse | null {
  if (!orgId || !siteId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(getAcceptanceProofCacheKey(orgId, siteId, deviceId, scopeKey))
      || (scopeKey ? window.localStorage.getItem(getAcceptanceProofCacheKey(orgId, siteId, deviceId)) : null);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as ReliabilityProofPackPreviewResponse) : null;
  } catch {
    return null;
  }
}

export function writeAcceptanceProofCache(
  orgId: string,
  siteId: string,
  value: ReliabilityProofPackPreviewResponse,
  deviceId?: string,
  scopeKey?: string
): void {
  if (!orgId || !siteId || typeof window === 'undefined') return;
  try {
    const serialized = JSON.stringify(value);
    window.localStorage.setItem(getAcceptanceProofCacheKey(orgId, siteId, deviceId, scopeKey), serialized);
    if (scopeKey) {
      window.localStorage.setItem(getAcceptanceProofCacheKey(orgId, siteId, deviceId), serialized);
    }
  } catch {
    // ignore storage quota / private mode issues
  }
}

export type ArtifactPostureTone = 'success' | 'warning' | 'default';

export type ArtifactPostureRow = {
  id: 'cra' | 'sra' | 'proof_pack' | 'handoff';
  label: string;
  stateLabel: string;
  tone: ArtifactPostureTone;
  detail: string;
};

export type ArtifactPostureSummary = {
  summaryLabel: string;
  summaryTone: ArtifactPostureTone;
  rows: ArtifactPostureRow[];
};

export type ArtifactPostureSummaryInput = {
  devicesInScope: number;
  camerasOnline: number;
  zonesConfigured: number;
  policiesStaged: number;
  activeAlertCount: number;
  proofPreview: ReliabilityProofPackPreviewResponse | null;
};

export function getReportVisualById(
  visuals: ReportVisual[] | null | undefined,
  visualId: string
): ReportVisual | null {
  if (!Array.isArray(visuals)) return null;
  return visuals.find((visual) => String(visual?.id || '').trim() === visualId) || null;
}

export function getScorecardMetricValue(
  visuals: ReportVisual[] | null | undefined,
  visualId: string,
  metricId: string
): number | null {
  const visual = getReportVisualById(visuals, visualId);
  if (!visual || visual.type !== 'scorecard') return null;
  const items = Array.isArray(visual.data?.items) ? visual.data.items : [];
  const item = items.find((candidate: any) => String(candidate?.id || '').trim() === metricId);
  if (!item) return null;
  const numeric = Number(item?.value);
  return Number.isFinite(numeric) ? numeric : null;
}

function pluralize(value: number, noun: string): string {
  return `${value} ${noun}${value === 1 ? '' : 's'}`;
}

function formatPendingSetupBlockers(input: {
  camerasOnline: number;
  zonesConfigured: number;
  policiesStaged: number;
}): string {
  const blockers: string[] = [];
  if (input.camerasOnline <= 0) blockers.push('camera feeds');
  if (input.zonesConfigured <= 0) blockers.push('zones');
  if (input.policiesStaged <= 0) blockers.push('policies');
  if (blockers.length === 0) return 'rollout inputs';
  if (blockers.length === 1) return blockers[0];
  if (blockers.length === 2) return `${blockers[0]} and ${blockers[1]}`;
  return `${blockers[0]}, ${blockers[1]}, and ${blockers[2]}`;
}

export function deriveArtifactPostureSummary(input: ArtifactPostureSummaryInput): ArtifactPostureSummary {
  const devicesInScope = Math.max(0, Number(input.devicesInScope || 0));
  const camerasOnline = Math.max(0, Number(input.camerasOnline || 0));
  const zonesConfigured = Math.max(0, Number(input.zonesConfigured || 0));
  const policiesStaged = Math.max(0, Number(input.policiesStaged || 0));
  const activeAlertCount = Math.max(0, Number(input.activeAlertCount || 0));
  const proofPreview = input.proofPreview;
  const proofBlockingVisualCount =
    getScorecardMetricValue(proofPreview?.report_visuals, 'case_status_scorecard', 'high_or_critical_total') ?? null;

  const hasDevices = devicesInScope > 0;
  const craAvailable = hasDevices && camerasOnline > 0;
  const sraAvailable = craAvailable && zonesConfigured > 0 && policiesStaged > 0;
  const proofReady = Boolean(proofPreview?.case_file || proofPreview?.ok);
  const proofBlockingIncidents = Math.max(
    0,
    Number(proofBlockingVisualCount ?? proofPreview?.case_file?.summary?.high_or_critical_total ?? 0)
  );
  const handoffReady = sraAvailable && activeAlertCount === 0 && proofReady;

  const rows: ArtifactPostureRow[] = [
    !hasDevices
      ? {
          id: 'cra',
          label: REPORT_KIND_LABELS.cra_readiness,
          stateLabel: 'Not started',
          tone: 'default',
          detail: 'Bring a device online before the camera readiness report appears in Acceptance.',
        }
      : craAvailable
        ? {
          id: 'cra',
          label: REPORT_KIND_LABELS.cra_readiness,
          stateLabel: 'Available',
          tone: 'success',
          detail: `${pluralize(camerasOnline, 'camera')} online. The camera readiness report is ready in Acceptance.`,
        }
      : {
          id: 'cra',
          label: REPORT_KIND_LABELS.cra_readiness,
          stateLabel: 'Pending',
          tone: 'warning',
          detail: 'Devices are visible, but camera feeds are not online yet.',
        },
    !hasDevices
      ? {
          id: 'sra',
          label: REPORT_KIND_LABELS.site_readiness,
          stateLabel: 'Not started',
          tone: 'default',
          detail: 'The site readiness review appears in Acceptance after rollout signals exist for this site.',
        }
      : sraAvailable
        ? {
          id: 'sra',
          label: REPORT_KIND_LABELS.site_readiness,
          stateLabel: 'Available',
          tone: 'success',
          detail: 'Zones and policies are staged. Review the site readiness review in Acceptance.',
        }
      : {
          id: 'sra',
          label: REPORT_KIND_LABELS.site_readiness,
          stateLabel: 'Pending',
          tone: 'warning',
          detail: `Finish ${formatPendingSetupBlockers({ camerasOnline, zonesConfigured, policiesStaged })} before the site readiness review is available.`,
        },
    proofReady
      ? {
          id: 'proof_pack',
          label: 'Incident evidence package review',
          stateLabel: 'Available',
          tone: 'success',
          detail:
            proofBlockingIncidents > 0
              ? `Cached reporting period still shows ${pluralize(proofBlockingIncidents, 'high/critical incident')}.`
              : 'Evidence package posture is available for Acceptance review.',
        }
      : !hasDevices
      ? {
          id: 'proof_pack',
          label: 'Incident evidence package review',
          stateLabel: 'Not started',
          tone: 'default',
          detail: 'Acceptance will show proof posture once the site has live setup context.',
        }
        : activeAlertCount > 0
      ? {
          id: 'proof_pack',
          label: 'Incident evidence package review',
          stateLabel: 'Pending',
          tone: 'warning',
          detail: `${pluralize(activeAlertCount, 'active alert')} are still active, but the incident evidence package can still capture those unresolved items for review.`,
        }
      : {
          id: 'proof_pack',
          label: 'Incident evidence package review',
          stateLabel: 'Pending',
          tone: 'warning',
          detail: 'Incident reporting opens once setup context is available for this scope.',
        },
    handoffReady
      ? {
          id: 'handoff',
          label: 'Customer handoff bundle',
          stateLabel: 'Available',
          tone: 'success',
          detail: 'Proof and readiness posture are clear enough to review customer handoff in Acceptance.',
        }
      : !hasDevices
      ? {
          id: 'handoff',
          label: 'Customer handoff bundle',
          stateLabel: 'Not started',
          tone: 'default',
          detail: 'Customer handoff stays inactive until the site has rollout and proof posture.',
        }
        : activeAlertCount > 0
      ? {
          id: 'handoff',
          label: 'Customer handoff bundle',
          stateLabel: 'Pending',
          tone: 'warning',
          detail: 'You can still draft the customer handoff bundle, but final signoff stays blocked until the active issues are resolved.',
        }
      : !proofReady
      ? {
          id: 'handoff',
          label: 'Customer handoff bundle',
          stateLabel: 'Pending',
          tone: 'warning',
          detail: 'Proof review should land first in Acceptance.',
        }
      : {
          id: 'handoff',
          label: 'Customer handoff bundle',
          stateLabel: 'Pending',
          tone: 'warning',
          detail: 'Customer handoff stays pending until the camera readiness report and site readiness review are available.',
        },
  ];

  if (handoffReady) {
    return {
      summaryLabel: 'Handoff ready',
      summaryTone: 'success',
      rows,
    };
  }
  if (activeAlertCount > 0) {
    return {
      summaryLabel: 'Proof handoff pending',
      summaryTone: 'warning',
      rows,
    };
  }
  if (proofReady) {
    return {
      summaryLabel: 'Proof visible',
      summaryTone: 'success',
      rows,
    };
  }
  if (hasDevices) {
    return {
      summaryLabel: 'Proof handoff pending',
      summaryTone: 'warning',
      rows,
    };
  }
  return {
    summaryLabel: 'Not started',
    summaryTone: 'default',
    rows,
  };
}
